import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Package, Plus, TrendingUp, Eye } from 'lucide-react';
import { bagsApi, productsApi } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const AdminDashboard = () => {
  const [stats, setStats] = useState({ bags: 0, products: 0 });
  const [recentBags, setRecentBags] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    return `${BACKEND_URL}${imagePath}`;
  };

  const fetchStats = async () => {
    try {
      setLoading(true);

      const [bagsRes, productsRes] = await Promise.all([
        bagsApi.getAll(),
        productsApi.getAll(),
      ]);

      const bagsData = Array.isArray(bagsRes.data)
        ? bagsRes.data
        : Array.isArray(bagsRes.data?.data)
        ? bagsRes.data.data
        : Array.isArray(bagsRes.data?.bags)
        ? bagsRes.data.bags
        : [];

      const productsData = Array.isArray(productsRes.data)
        ? productsRes.data
        : Array.isArray(productsRes.data?.data)
        ? productsRes.data.data
        : Array.isArray(productsRes.data?.products)
        ? productsRes.data.products
        : [];

      setStats({
        bags: bagsData.length,
        products: productsData.length,
      });

      setRecentBags(bagsData.slice(0, 5));
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner text="Panel yüklənir..." />;
  }

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="font-serif text-3xl text-brand-brown">Xoş gəlmisiniz!</h1>
        <p className="text-brand-muted mt-2">Turkan Rustamova brendinin idarə paneli</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div
          className="bg-white rounded-2xl p-6 shadow-card hover:shadow-card-hover transition-all duration-300"
          data-testid="stat-bags"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-brand-muted mb-1">Ümumi çanta</p>
              <p className="font-serif text-4xl text-brand-brown">{stats.bags}</p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-brand-accent/30 flex items-center justify-center">
              <ShoppingBag className="w-7 h-7 text-brand-primary" />
            </div>
          </div>
          <Link
            to="/admin/bags"
            className="inline-flex items-center gap-2 mt-4 text-sm text-brand-primary hover:underline"
          >
            Hamısına bax
            <Eye className="w-4 h-4" />
          </Link>
        </div>

        <div
          className="bg-white rounded-2xl p-6 shadow-card hover:shadow-card-hover transition-all duration-300"
          data-testid="stat-products"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-brand-muted mb-1">Ümumi məhsul</p>
              <p className="font-serif text-4xl text-brand-brown">{stats.products}</p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-brand-secondary/50 flex items-center justify-center">
              <Package className="w-7 h-7 text-brand-brown" />
            </div>
          </div>
          <Link
            to="/admin/products"
            className="inline-flex items-center gap-2 mt-4 text-sm text-brand-primary hover:underline"
          >
            Hamısına bax
            <Eye className="w-4 h-4" />
          </Link>
        </div>

        <div
          className="bg-white rounded-2xl p-6 shadow-card hover:shadow-card-hover transition-all duration-300"
          data-testid="stat-total"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-brand-muted mb-1">Ümumi artım</p>
              <p className="font-serif text-4xl text-green-600">
                +{stats.bags + stats.products}
              </p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-green-100 flex items-center justify-center">
              <TrendingUp className="w-7 h-7 text-green-600" />
            </div>
          </div>
          <p className="mt-4 text-sm text-brand-muted">Cəmi inventar</p>
        </div>

        <div
          className="bg-gradient-to-br from-brand-primary to-[#E08E73] rounded-2xl p-6 text-white"
          data-testid="stat-quick"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-white/70 mb-1">Tez əməliyyat</p>
              <p className="font-serif text-xl">Yeni əlavə et</p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
              <Plus className="w-7 h-7" />
            </div>
          </div>
          <div className="flex gap-2">
            <Link
              to="/admin/add-bag"
              className="text-xs bg-white/20 hover:bg-white/30 px-4 py-2 rounded-full transition-colors"
              data-testid="quick-add-bag"
            >
              + Çanta
            </Link>
            <Link
              to="/admin/add-product"
              className="text-xs bg-white/20 hover:bg-white/30 px-4 py-2 rounded-full transition-colors"
              data-testid="quick-add-product"
            >
              + Məhsul
            </Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-2xl p-6 shadow-card" data-testid="recent-bags">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-xl text-brand-brown">Son çantalar</h2>
            <Link to="/admin/bags" className="text-sm text-brand-primary hover:underline">
              Hamısı →
            </Link>
          </div>

          {recentBags.length === 0 ? (
            <p className="text-brand-muted text-center py-8">Hələ çanta əlavə olunmayıb</p>
          ) : (
            <div className="space-y-4">
              {recentBags.map((bag, index) => (
                <div
                  key={bag._id || index}
                  className="flex items-center gap-4 p-3 rounded-xl hover:bg-brand-accent/10 transition-colors"
                >
                  <div className="w-14 h-14 rounded-xl bg-brand-muted/10 overflow-hidden flex-shrink-0">
                    {bag.bagImg ? (
                      <img
                        src={getImageUrl(bag.bagImg)}
                        alt={bag.bagAd}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ShoppingBag className="w-6 h-6 text-brand-muted" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-brand-brown truncate">
                      {bag.bagAd || 'Adsız'}
                    </p>
                    <p className="text-xs text-brand-muted">
                      {bag.bagCategory || 'Kateqoriyasız'}
                    </p>
                  </div>

                  <p className="font-serif text-brand-primary">
                    {Number(bag.bagPrice || 0).toFixed(2)} ₼
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-card" data-testid="quick-links">
          <h2 className="font-serif text-xl text-brand-brown mb-6">Tez keçidlər</h2>
          <div className="grid grid-cols-2 gap-4">
            <Link
              to="/admin/bags"
              className="p-5 rounded-2xl border-2 border-brand-secondary/50 hover:border-brand-primary hover:bg-brand-accent/10 transition-all text-center group"
            >
              <ShoppingBag className="w-8 h-8 mx-auto mb-2 text-brand-muted group-hover:text-brand-primary transition-colors" />
              <p className="text-sm font-medium text-brand-brown">Çantaları idarə et</p>
            </Link>

            <Link
              to="/admin/add-bag"
              className="p-5 rounded-2xl border-2 border-brand-secondary/50 hover:border-brand-primary hover:bg-brand-accent/10 transition-all text-center group"
            >
              <Plus className="w-8 h-8 mx-auto mb-2 text-brand-muted group-hover:text-brand-primary transition-colors" />
              <p className="text-sm font-medium text-brand-brown">Yeni çanta</p>
            </Link>

            <Link
              to="/admin/products"
              className="p-5 rounded-2xl border-2 border-brand-secondary/50 hover:border-brand-primary hover:bg-brand-accent/10 transition-all text-center group"
            >
              <Package className="w-8 h-8 mx-auto mb-2 text-brand-muted group-hover:text-brand-primary transition-colors" />
              <p className="text-sm font-medium text-brand-brown">Məhsulları idarə et</p>
            </Link>

            <Link
              to="/admin/add-product"
              className="p-5 rounded-2xl border-2 border-brand-secondary/50 hover:border-brand-primary hover:bg-brand-accent/10 transition-all text-center group"
            >
              <Plus className="w-8 h-8 mx-auto mb-2 text-brand-muted group-hover:text-brand-primary transition-colors" />
              <p className="text-sm font-medium text-brand-brown">Yeni məhsul</p>
            </Link>
          </div>

          <Link
            to="/"
            target="_blank"
            className="block text-center mt-6 p-4 bg-brand-cream rounded-xl hover:bg-brand-accent/20 transition-colors text-sm text-brand-muted hover:text-brand-primary"
          >
            Sayta bax →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;